import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { withRouter } from 'react-router';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { axiosCaller } from '../../../utils/apiCaller';
import MikaPageHeader from '../../../components/MikaPageHeader/MikaPageHeader';
import MikaDateFilter, { DateRange } from '../../../components/MikaDateFilter/MikaDateFilter';
import MikaFileManager, { MikaFile } from '../../../components/MikaFileManager/MikaFileManager';
import CommentThread from './CommentThread';
import ProductRow from './ProductRow';
import { STATUS_MAP } from './NewProductionBoard';
import moment from 'moment';
import { formatPrice } from '../../../utils/react';
import '../../Contact/Contact.scss';
import '../../Offers/Offers.scss';
import './NewBoard.scss';

interface Department { value: number; label: string; }
interface Responsibility { id: number; label: string; }

// ── Default date range: last 30 days rolling ──
const getLast30DaysRange = (): DateRange => {
    const today = new Date();
    const start = new Date();
    start.setDate(today.getDate() - 29);
    const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { from: fmt(start), to: fmt(today) } as DateRange;
};

const NewArchiveBoard: React.FC<{ history?: any }> = ({ history }) => {
    const intl = useIntl();
    const fm = useCallback(
        (id: string, defaultMessage: string, values?: Record<string, any>) =>
            intl.formatMessage({ id, defaultMessage }, values),
        [intl]
    );

    // Redux: populated by Production.tsx mount via getUserPermissionsAndResponsibilities.
    const permissions = useSelector((state: any) => state?.app?.permissions || {});
    const adminDepartments = useSelector((state: any) => state?.app?.adminDepartments || []);

    const [products, setProducts] = useState<any[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [responsibilities, setResponsibilities] = useState<Responsibility[]>([]);
    const [selectedDeptIds, setSelectedDeptIds] = useState<string[]>([]);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedBox, setSelectedBox] = useState<{ productId: number; responsibilityId: number } | null>(null);

    const [userResponsibilityIds, setUserResponsibilityIds] = useState<number[]>([]);
    const [authReady, setAuthReady] = useState(false);

    // "Every" rule — admin view only when fullPrivileges or every selected dept is admined.
    const isAdmin = useMemo(() => {
        if (permissions?.fullPrivileges) return true;
        if (!selectedDeptIds.length) return false;
        return selectedDeptIds.every((id) =>
            adminDepartments?.some((d: any) => Number(d?.departmentId) === Number(id) && d?.isAdmin)
        );
    }, [permissions, adminDepartments, selectedDeptIds]);

    const [detailProduct, setDetailProduct] = useState<any>(null);
    const [detailFiles, setDetailFiles] = useState<MikaFile[]>([]);
    const [detailFilesLoading, setDetailFilesLoading] = useState(false);

    const STATUS_FILTER_OPTIONS = useMemo(() => [
        { value: '1', label: fm('production.status.completed', 'Përfunduar') },
        { value: '2', label: fm('production.status.toDo', 'Për tu realizuar') },
        { value: '3', label: fm('production.status.late', 'Me vonesë') },
        { value: '4', label: fm('production.status.inProcess', 'Në proces') },
        { value: '5', label: fm('production.status.paused', 'Pauzuar') },
        { value: '9', label: fm('production.status.nonActive', 'Jo aktiv') },
    ], [fm]);

    const openProductDetail = (product: any) => {
        setDetailProduct(product);
        setDetailFilesLoading(true);
        axiosCaller('GET', `project-blueprint/${product.productId}/products`).then(res => {
            const files = (res?.data?.data || []).map((f: any) => ({
                id: f.id,
                name: f.nameOfBlueprint || 'file',
                url: f.blueprint,
            }));
            setDetailFiles(files);
            setDetailFilesLoading(false);
        }).catch(() => setDetailFilesLoading(false));
    };

    const closeProductDetail = () => {
        setDetailProduct(null);
        setDetailFiles([]);
    };

    const [zoomLvl, setZoomLvl] = useState(100);
    const controlZoom = (increase: boolean) => {
        setZoomLvl(prev => increase ? Math.min(prev + 10, 150) : Math.max(prev - 10, 10));
    };

    const [colWidths, setColWidths] = useState({ client: 100, project: 100, product: 175, qty: 50 });
    const resizing = useRef<{ col: string; startX: number; startWidth: number } | null>(null);

    const onResizeStart = (col: string, e: React.MouseEvent) => {
        e.preventDefault();
        resizing.current = { col, startX: e.clientX, startWidth: (colWidths as any)[col] };
        const onMove = (ev: MouseEvent) => {
            if (!resizing.current) return;
            const diff = ev.clientX - resizing.current.startX;
            const newWidth = Math.max(50, resizing.current.startWidth + diff);
            setColWidths(prev => ({ ...prev, [resizing.current!.col]: newWidth }));
        };
        const onUp = () => { resizing.current = null; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    const [statusFilters, setStatusFilters] = useState<string[]>([]);
    const [responsibilityFilters, setResponsibilityFilters] = useState<string[]>([]);
    const filterDatesBy = 'completed';
    const sortBy = 'completed';
    const [dateRange, setDateRange] = useState<DateRange | null>(getLast30DaysRange());
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

    const [processDropOpen, setProcessDropOpen] = useState(false);
    const [statusDropOpen, setStatusDropOpen] = useState(false);
    const [sortDropOpen, setSortDropOpen] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(timer);
    }, [search]);

    const hasActiveFilters = statusFilters.length > 0 || responsibilityFilters.length > 0 || dateRange;

    useEffect(() => {
        axiosCaller('GET', 'all-departments').then(res => {
            setDepartments((res?.data?.data || []).map((d: any) => ({ value: d.value || d.id, label: d.label || d.name })));
        });
        axiosCaller('GET', 'responsibilities').then(res => {
            setResponsibilities((res?.data?.data || []).map((r: any) => ({ id: r.id || r.value, label: r.label || r.name })));
        });

        axiosCaller('GET', 'authenticated').then(res => {
            setUserResponsibilityIds(res?.data?.responsibilities || []);
            setAuthReady(true);
        }).catch(() => setAuthReady(true));

        const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setSelectedBox(null); };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const filterQsRef = useRef('');

    const buildFilterQs = useCallback(() => {
        let qs = '';
        selectedDeptIds.forEach(id => { qs += `&departmentId[]=${id}`; });
        if (debouncedSearch) qs += `&name=${encodeURIComponent(debouncedSearch)}`;
        statusFilters.forEach(id => { qs += `&responsibilityStatusId[]=${id}`; });
        responsibilityFilters.forEach(id => { qs += `&responsibilityId[]=${id}`; });
        if (dateRange?.from) qs += `&start=${dateRange.from}`;
        if (dateRange?.to) qs += `&end=${dateRange.to}`;
        if ((dateRange?.from || dateRange?.to) && filterDatesBy) qs += `&filterDatesBy=${filterDatesBy}`;
        if (sortBy) qs += `&sort=${sortBy}`;
        if (sortDir) qs += `&sortDir=${sortDir}`;
        if (!isAdmin) qs += '&forUser=1';
        filterQsRef.current = qs;
        return qs;
    }, [isAdmin, selectedDeptIds, debouncedSearch, statusFilters, responsibilityFilters, dateRange, filterDatesBy, sortBy, sortDir]);

    const fetchProductsFull = useCallback(async () => {
        const qs = `clients/products-statuses?moduleId=5&active=1&done=1${filterQsRef.current}`;
        try {
            const res = await axiosCaller('GET', qs);
            const raw = res?.data?.data || res?.data || [];
            const arr = Array.isArray(raw) ? raw : [];
            const transformed = arr.map((p: any) => transformProduct(p)).filter((p: any) => p.statuses.length > 0);
            setProducts(transformed);
        } catch {}
        setLoading(false);
    }, []);

    useEffect(() => {
        if (!authReady) return;
        buildFilterQs();
        setLoading(true);
        setProducts([]);
        fetchProductsFull();
    }, [buildFilterQs, authReady]);

    const transformProduct = (p: any) => {
        const latestComments = p.latestComments || {};
        const statuses = (p.statuses || []).map((s: any) => {
            const responsibilityId = s.responsibility?.id;
            const materialStatuses = (p.materialStatuses || []).filter(
                (m: any) => m.responsibilityId === responsibilityId
            ).map((m: any) => ({ ...m, trackers: m.trackers || null }));
            const hasMaterials = materialStatuses.length > 0;
            const completedMaterials = materialStatuses.filter((m: any) => m.responsibilityStatusId === 1).length;
            const latestComment = latestComments[`${responsibilityId}_0`] || null;
            return {
                id: s.id,
                responsibilityId,
                responsibilityName: s.responsibility?.label,
                responsibilityStatusId: s.responsibilityStatusId,
                statusInfo: STATUS_MAP[s.responsibilityStatusId] || STATUS_MAP[9],
                userName: s.responsibility?.userName,
                updatedAt: s.updatedAt,
                warning: s.warning,
                adminWarning: s.adminWarning,
                otherComment: s.otherComment || null,
                latestComment,
                hasMaterials,
                materialCount: materialStatuses.length,
                completedMaterials,
                materialStatuses,
            };
        });
        return {
            productId: p.id,
            clientId: p.client?.id,
            clientName: p.client?.name,
            projectId: p.project?.id,
            projectName: p.project?.name,
            productName: p.designation?.name,
            productCode: p.type,
            quantity: p.quantity,
            unit: p.designation?.unit?.name,
            start: p.start,
            end: p.end,
            calendarComment: p.calendarComment,
            offerBy: p.offerCreated?.userName,
            measureStatus: p.measureStatus,
            totalPrice: p.discountedPrice || p.totalPrice,
            completedProcesses: p.completedProcesses,
            totalProcesses: p.totalProcesses,
            productionStartedAt: p.productionStartedAt,
            statuses,
        };
    };

    const filteredProducts = products;

    const clearFilters = () => {
        setStatusFilters([]);
        setResponsibilityFilters([]);
        setDateRange(null);
    };

    const toggleMultiFilter = (list: string[], setList: (v: string[]) => void, value: string) => {
        setList(list.includes(value) ? list.filter(v => v !== value) : [...list, value]);
    };

    useEffect(() => {
        const handler = () => { setProcessDropOpen(false); setStatusDropOpen(false); setSortDropOpen(false); };
        if (processDropOpen || statusDropOpen || sortDropOpen) {
            setTimeout(() => document.addEventListener('click', handler), 0);
            return () => document.removeEventListener('click', handler);
        }
    }, [processDropOpen, statusDropOpen, sortDropOpen]);

    return (
        <div className="npb-container">
            <MikaPageHeader
                title={fm('production.archive.title', 'Arkiva e prodhimit')}
                subtitle={`${filteredProducts.length} ${fm('production.board.subtitleProducts', 'produkte')}`}
            />

            <div className="npb-dept-strip">
                <button className={`npb-dept-btn ${selectedDeptIds.length === 0 ? 'active' : ''}`} onClick={() => setSelectedDeptIds([])} type="button">
                    <FormattedMessage id="common.pagination.all" defaultMessage="Të gjitha" />
                </button>
                {departments.map(d => (
                    <button key={d.value} className={`npb-dept-btn ${selectedDeptIds.includes(String(d.value)) ? 'active' : ''}`} onClick={() => toggleMultiFilter(selectedDeptIds, setSelectedDeptIds, String(d.value))} type="button">
                        {d.label}
                    </button>
                ))}
            </div>

            <div className="clp-compact-toolbar">
                <div className="clp-search-wrap">
                    <svg className="clp-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input type="text" className="clp-search-input" placeholder={fm('production.board.searchPlaceholder', 'Kërko produkte...')}
                        value={search} onChange={e => setSearch(e.target.value)} />
                    {search && (
                        <button className="clp-search-clear" onClick={() => setSearch('')} type="button">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                    )}
                </div>
                <div className="clp-toolbar-actions">
                    <div className="npb-dropdown-wrap" onClick={e => e.stopPropagation()}>
                        <button className={`npb-toolbar-btn ${processDropOpen ? 'active' : ''} ${responsibilityFilters.length > 0 ? 'has-value' : ''}`} onClick={() => { setProcessDropOpen(!processDropOpen); setStatusDropOpen(false); setSortDropOpen(false); }} type="button">
                            <FormattedMessage id="production.board.processes" defaultMessage="Proceset" />
                            {responsibilityFilters.length > 0 && <span className="npb-toolbar-badge">{responsibilityFilters.length}</span>}
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
                        </button>
                        {processDropOpen && (
                            <div className="npb-dropdown npb-dropdown-multi">
                                {responsibilities.map(r => (
                                    <button key={r.id} className={`npb-dropdown-item ${responsibilityFilters.includes(String(r.id)) ? 'active' : ''}`} onClick={() => toggleMultiFilter(responsibilityFilters, setResponsibilityFilters, String(r.id))} type="button">
                                        <span className="npb-dropdown-check">{responsibilityFilters.includes(String(r.id)) ? '✓' : ''}</span>
                                        {r.label}
                                    </button>
                                ))}
                                {responsibilityFilters.length > 0 && (
                                    <button className="npb-dropdown-clear" onClick={() => setResponsibilityFilters([])} type="button">
                                        <FormattedMessage id="production.board.clear" defaultMessage="Pastro" />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="npb-dropdown-wrap" onClick={e => e.stopPropagation()}>
                        <button className={`npb-toolbar-btn ${statusDropOpen ? 'active' : ''} ${statusFilters.length > 0 ? 'has-value' : ''}`} onClick={() => { setStatusDropOpen(!statusDropOpen); setProcessDropOpen(false); setSortDropOpen(false); }} type="button">
                            <FormattedMessage id="production.board.statuses" defaultMessage="Statuset" />
                            {statusFilters.length > 0 && <span className="npb-toolbar-badge">{statusFilters.length}</span>}
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
                        </button>
                        {statusDropOpen && (
                            <div className="npb-dropdown npb-dropdown-multi">
                                {STATUS_FILTER_OPTIONS.map(o => (
                                    <button key={o.value} className={`npb-dropdown-item ${statusFilters.includes(o.value) ? 'active' : ''}`} onClick={() => toggleMultiFilter(statusFilters, setStatusFilters, o.value)} type="button">
                                        <span className="npb-dropdown-check">{statusFilters.includes(o.value) ? '✓' : ''}</span>
                                        {o.label}
                                    </button>
                                ))}
                                {statusFilters.length > 0 && (
                                    <button className="npb-dropdown-clear" onClick={() => setStatusFilters([])} type="button">
                                        <FormattedMessage id="production.board.clear" defaultMessage="Pastro" />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="npb-dropdown-wrap" onClick={e => e.stopPropagation()}>
                        <button className={`npb-toolbar-btn ${sortDropOpen ? 'active' : ''}`} onClick={() => { setSortDropOpen(!sortDropOpen); setProcessDropOpen(false); setStatusDropOpen(false); }} type="button">
                            <FormattedMessage id="production.board.sortBy" defaultMessage="Renditja" />
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
                        </button>
                        {sortDropOpen && (
                            <div className="npb-dropdown">
                                <button className={`npb-dropdown-item ${sortDir === 'desc' ? 'active' : ''}`} onClick={() => { setSortDir('desc'); setSortDropOpen(false); }} type="button">
                                    <FormattedMessage id="production.archive.newestFirst" defaultMessage="Më të rejat së pari" />
                                </button>
                                <button className={`npb-dropdown-item ${sortDir === 'asc' ? 'active' : ''}`} onClick={() => { setSortDir('asc'); setSortDropOpen(false); }} type="button">
                                    <FormattedMessage id="production.archive.oldestFirst" defaultMessage="Më të vjetrat së pari" />
                                </button>
                            </div>
                        )}
                    </div>

                    <MikaDateFilter
                        value={dateRange}
                        onChange={(r) => setDateRange(r)}
                    />

                    {hasActiveFilters && (
                        <button className="npb-clear-all" onClick={clearFilters} type="button" title={fm('production.board.clearFilters', 'Pastro filtrat')}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                    )}

                    <div className="npb-zoom-control">
                        <button type="button" className="npb-zoom-btn" onClick={() => controlZoom(false)} title={fm('production.board.zoomOut', 'Zvogëlo')}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 512 512" fill="currentColor"><path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM136 184c-13.3 0-24 10.7-24 24s10.7 24 24 24H280c13.3 0 24-10.7 24-24s-10.7-24-24-24H136z"/></svg>
                        </button>
                        <span className="npb-zoom-label">{zoomLvl}%</span>
                        <button type="button" className="npb-zoom-btn" onClick={() => controlZoom(true)} title={fm('production.board.zoomIn', 'Zmadho')}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 512 512" fill="currentColor"><path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM184 296c0 13.3 10.7 24 24 24s24-10.7 24-24V232h64c13.3 0 24-10.7 24-24s-10.7-24-24-24H232V120c0-13.3-10.7-24-24-24s-24 10.7-24 24v64H120c-13.3 0-24 10.7-24 24s10.7 24 24 24h64v64z"/></svg>
                        </button>
                    </div>
                </div>
            </div>

            <div className="npb-board">
                <div className="npb-board-inner" style={{ zoom: `${zoomLvl}%` }}>
                <div className="npb-header">
                    <div className="npb-header-sticky">
                        <span className="npb-header-cell npb-col-num">#</span>
                        <span className="npb-header-cell" style={{ width: colWidths.client, minWidth: 50 }}>
                            <FormattedMessage id="production.board.clientCol" defaultMessage="Klienti" />
                            <span className="npb-resize-handle" onMouseDown={e => onResizeStart('client', e)} />
                        </span>
                        <span className="npb-header-cell" style={{ width: colWidths.project, minWidth: 50 }}>
                            <FormattedMessage id="production.board.projectCol" defaultMessage="Projekti" />
                            <span className="npb-resize-handle" onMouseDown={e => onResizeStart('project', e)} />
                        </span>
                        <span className="npb-header-cell" style={{ width: colWidths.product, minWidth: 50 }}>
                            <FormattedMessage id="production.board.productCol" defaultMessage="Produkti" />
                            <span className="npb-resize-handle" onMouseDown={e => onResizeStart('product', e)} />
                        </span>
                        <span className="npb-header-cell" style={{ width: colWidths.qty, minWidth: 50 }}>
                            <FormattedMessage id="production.board.quantityCol" defaultMessage="Sasia" />
                            <span className="npb-resize-handle" onMouseDown={e => onResizeStart('qty', e)} />
                        </span>
                    </div>
                    <span className="npb-header-cell npb-col-statuses">
                        <FormattedMessage id="production.board.processesCol" defaultMessage="Proceset" />
                    </span>
                </div>

                {loading ? (
                    <div className="npb-loading">
                        <div className="npb-spinner" />
                        <span><FormattedMessage id="production.board.loading" defaultMessage="Duke ngarkuar..." /></span>
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <div className="npb-empty"><FormattedMessage id="common.noData" defaultMessage="Nuk ka të dhëna" /></div>
                ) : (
                    <div className="npb-rows">
                        {filteredProducts.map((product, index) => (
                            <ProductRow
                                key={product.productId}
                                product={product}
                                index={index}
                                isAdmin={isAdmin}
                                userResponsibilityIds={userResponsibilityIds}
                                colWidths={colWidths}
                                selectedBox={selectedBox}
                                onRowClick={() => openProductDetail(product)}
                                onBoxClick={(responsibilityId: number) => {
                                    const isSame = selectedBox?.productId === product.productId && selectedBox?.responsibilityId === responsibilityId;
                                    setSelectedBox(isSame ? null : { productId: product.productId, responsibilityId });
                                }}
                                onClose={() => setSelectedBox(null)}
                                onStatusUpdated={() => fetchProductsFull()}
                                readOnly
                            />
                        ))}
                    </div>
                )}
                </div>
            </div>

            <div className="npb-legend">
                <span className="npb-legend-item"><span className="npb-legend-dot npb-legend-completed" /><FormattedMessage id="production.status.completed" defaultMessage="Përfunduar" /></span>
                <span className="npb-legend-item"><span className="npb-legend-dot npb-legend-toDo" /><FormattedMessage id="production.status.toDo" defaultMessage="Për tu realizuar" /></span>
                <span className="npb-legend-item"><span className="npb-legend-dot npb-legend-late" /><FormattedMessage id="production.status.late" defaultMessage="Me vonesë" /></span>
                <span className="npb-legend-item"><span className="npb-legend-dot npb-legend-inProcess" /><FormattedMessage id="production.status.inProcess" defaultMessage="Në proces" /></span>
                <span className="npb-legend-item"><span className="npb-legend-dot npb-legend-paused" /><FormattedMessage id="production.status.paused" defaultMessage="Pauzuar" /></span>
                <span className="npb-legend-item"><span className="npb-legend-dot npb-legend-nonActive" /><FormattedMessage id="production.status.nonActive" defaultMessage="Jo aktiv" /></span>
            </div>

            {detailProduct && (
                <div className="npb-detail-overlay" onClick={closeProductDetail}>
                    <div className="npb-detail-popup" onClick={e => e.stopPropagation()}>
                        <div className="npb-detail-header">
                            <div className="npb-detail-header-left">
                                <span className="npb-detail-title">
                                    {detailProduct.productName}
                                    {detailProduct.productCode && <span className="npb-detail-code">({detailProduct.productCode})</span>}
                                </span>
                                {detailProduct.measureStatus === 2
                                    ? <span className="npb-detail-badge npb-detail-badge-green"><FormattedMessage id="production.detail.measureFinal" defaultMessage="Matje finale" /></span>
                                    : <span className="npb-detail-badge npb-detail-badge-amber"><FormattedMessage id="production.detail.measureInitial" defaultMessage="Matje fillestare" /></span>
                                }
                            </div>
                            <button className="npb-detail-close" onClick={closeProductDetail} type="button">&times;</button>
                        </div>

                        <div className="npb-detail-body">
                            <div className="npb-detail-info">
                                <div className="npb-detail-info-item">
                                    <span className="npb-detail-info-label"><FormattedMessage id="production.detail.client" defaultMessage="Klienti" /></span>
                                    <span className="npb-detail-info-value">{detailProduct.clientName || '\u2014'}</span>
                                </div>
                                <div className="npb-detail-info-item">
                                    <span className="npb-detail-info-label"><FormattedMessage id="production.detail.project" defaultMessage="Projekti" /></span>
                                    <span className="npb-detail-info-value">{detailProduct.projectName || '\u2014'}</span>
                                </div>
                                <div className="npb-detail-info-item">
                                    <span className="npb-detail-info-label"><FormattedMessage id="production.detail.offerBy" defaultMessage="Ofertën e bëri" /></span>
                                    <span className="npb-detail-info-value">{detailProduct.offerBy || '\u2014'}</span>
                                </div>
                                <div className="npb-detail-info-item">
                                    <span className="npb-detail-info-label"><FormattedMessage id="production.detail.quantity" defaultMessage="Sasia" /></span>
                                    <span className="npb-detail-info-value">{detailProduct.quantity}{detailProduct.unit ? ` ${detailProduct.unit}` : ''}</span>
                                </div>
                                {isAdmin && detailProduct.totalPrice && (
                                    <div className="npb-detail-info-item">
                                        <span className="npb-detail-info-label"><FormattedMessage id="production.detail.value" defaultMessage="Vlera" /></span>
                                        <span className="npb-detail-info-value" style={{ color: '#1e40af', fontWeight: 600 }}>{formatPrice(detailProduct.totalPrice, '€')}</span>
                                    </div>
                                )}
                            </div>

                            <div className="npb-detail-info" style={{ marginBottom: 14 }}>
                                <div className="npb-detail-info-item">
                                    <span className="npb-detail-info-label"><FormattedMessage id="production.detail.start" defaultMessage="Fillimi" /></span>
                                    <span className="npb-detail-info-value">{detailProduct.start ? moment(detailProduct.start).format('DD-MM-YYYY') : '\u2014'}</span>
                                </div>
                                <div className="npb-detail-info-item">
                                    <span className="npb-detail-info-label"><FormattedMessage id="production.detail.end" defaultMessage="Mbarimi" /></span>
                                    <span className="npb-detail-info-value">{detailProduct.end ? moment(detailProduct.end).format('DD-MM-YYYY') : '\u2014'}</span>
                                </div>
                            </div>

                            {detailProduct.productionStartedAt && (
                                <div className="npb-detail-production-badge">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                                    <span>
                                        <FormattedMessage id="production.detail.inProductionSince" defaultMessage="Në prodhim që nga: {date}" values={{ date: detailProduct.productionStartedAt }} />
                                    </span>
                                </div>
                            )}

                            {detailProduct.totalProcesses > 0 && (() => {
                                const completed = detailProduct.completedProcesses || 0;
                                const total = detailProduct.totalProcesses;
                                const pct = Math.round((completed / total) * 100);
                                const barColor = pct === 100 ? '#22c55e' : pct >= 50 ? '#3b82f6' : '#f59e0b';
                                return (
                                    <div className="npb-detail-progress">
                                        <div className="npb-detail-progress-header">
                                            <span className="npb-detail-progress-label"><FormattedMessage id="production.detail.progress" defaultMessage="Progresi" /></span>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: barColor }}>{completed}/{total} ({pct}%)</span>
                                        </div>
                                        <div className="npb-detail-progress-bar">
                                            <div className="npb-detail-progress-fill" style={{ width: `${pct}%`, background: barColor }} />
                                        </div>
                                    </div>
                                );
                            })()}

                            <div className="npb-detail-section">
                                <span className="npb-detail-section-label"><FormattedMessage id="production.detail.productComments" defaultMessage="Komentet e produktit" /></span>
                                <CommentThread
                                    productId={detailProduct.productId}
                                    responsibilityId={null}
                                    materialId={null}
                                    type="comment"
                                    label=""
                                    placeholder=""
                                    isAdmin={isAdmin}
                                    readOnly
                                />
                            </div>

                            <div className="npb-detail-section">
                                <span className="npb-detail-section-label"><FormattedMessage id="production.detail.files" defaultMessage="Skedarët" /></span>
                                <MikaFileManager
                                    files={detailFiles}
                                    loading={detailFilesLoading}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default withRouter(NewArchiveBoard as any);
